/**
 * barba-transitions.js
 * Theme-wide Barba.js page transitions.
 *
 * Load this file ONCE in layout/theme.liquid, after the Barba CDN script.
 * It registers a global slide-left transition and re-initialises any
 * section-specific JS (like the editorial categories section) after navigation.
 *
 * Required in theme.liquid <head>:
 *   <script src="https://unpkg.com/@barba/core@2.9.7/dist/barba.umd.min.js"></script>
 *   <script src="{{ 'barba-transitions.js' | asset_url }}" defer></script>
 *
 * Required in theme.liquid wrapping content_for_layout:
 *   <div data-barba="wrapper">
 *     <main data-barba="container" data-barba-namespace="{{ template.name }}">
 *       {{ content_for_layout }}
 *     </main>
 *   </div>
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────
     Guard: bail if Barba is not loaded
  ───────────────────────────────────── */
  if (typeof barba === 'undefined') {
    console.warn('[barba-transitions] barba.js not found. Ensure the CDN script loads before this file.');
    return;
  }

  /* ─────────────────────────────────────
     Helpers
  ───────────────────────────────────── */

  /**
   * Re-initialise all section JS after a Barba page transition.
   * Each section that needs re-init should expose its init function
   * on window (e.g. window.initEditorialCategories).
   */
  function reinitSections() {
    /* Editorial Categories section */
    if (typeof window.initEditorialCategories === 'function') {
      window.initEditorialCategories();
    }

    /* Add other section re-init calls here as you build more sections:
       if (typeof window.initHeroSlider === 'function') window.initHeroSlider();
    */
  }

  /**
   * Re-initialise Shopify theme features that reset on navigation.
   */
  function reinitTheme() {
    /* Dispatch the custom event Shopify themes use to re-bind sections */
    document.dispatchEvent(new CustomEvent('shopify:section:load'));
  }

  /* ─────────────────────────────────────
     Barba init
  ───────────────────────────────────── */
  barba.init({
    /* Prevent Barba from intercepting non-page links */
    prevent: function (_ref) {
      var el = _ref.el;
      /* Skip links that open in new tab, are anchors, or are admin/checkout */
      if (el.target === '_blank') return true;
      if (el.getAttribute('href') && el.getAttribute('href').charAt(0) === '#') return true;
      if (el.href && (el.href.indexOf('/cart') > -1 || el.href.indexOf('/checkout') > -1 || el.href.indexOf('/admin') > -1)) return true;
      return false;
    },

    transitions: [
      {
        name: 'slide-left',

        /* ── Leave: slide current page out to the left ── */
        leave: function (_ref) {
          var current = _ref.current;
          return new Promise(function (resolve) {
            /* Ensure GSAP is available */
            if (typeof gsap === 'undefined') {
              current.container.style.opacity = '0';
              setTimeout(resolve, 300);
              return;
            }

            gsap.to(current.container, {
              x: '-6vw',
              opacity: 0,
              duration: 0.45,
              ease: 'power2.inOut',
              onComplete: resolve
            });
          });
        },

        /* ── Enter: slide next page in from the right ── */
        enter: function (_ref) {
          var next = _ref.next;
          return new Promise(function (resolve) {
            if (typeof gsap === 'undefined') {
              next.container.style.opacity = '1';
              setTimeout(resolve, 300);
              return;
            }

            /* Start off-screen right */
            gsap.set(next.container, { x: '6vw', opacity: 0 });

            gsap.to(next.container, {
              x: 0,
              opacity: 1,
              duration: 0.5,
              ease: 'power2.out',
              onComplete: resolve
            });
          });
        }
      }
    ],

    /* ─────────────────────────────────────
       Global hooks
    ───────────────────────────────────── */
    views: [],

  });

  /* After every navigation: re-init sections and theme features */
  barba.hooks.afterEnter(function () {
    reinitSections();
    reinitTheme();

    /* Scroll to top of new page */
    window.scrollTo(0, 0);

    /* Re-register GSAP ScrollTrigger if present — it needs to
       recalculate scroll positions after DOM changes */
    if (typeof ScrollTrigger !== 'undefined') {
      ScrollTrigger.refresh();
    }
  });

  /* Clean up ScrollTrigger instances before leaving a page */
  barba.hooks.beforeLeave(function () {
    if (typeof ScrollTrigger !== 'undefined') {
      ScrollTrigger.getAll().forEach(function (st) { st.kill(); });
    }
  });

})();
